// [TOUR_PLANNER_V1] 매물 투어 동선 플래너
//   흐름: 선택 매물 → 현재 위치 받기 → 최적 순서 계산 → 목록 표시 → 구간별 내비 실행
//   연관: 백엔드 POST /api/tour/optimize  (순서 계산)
//         백엔드 POST /api/tour/geocode   (주소 추가)
//   주의: 카카오내비 실행은 모바일 + 카카오내비 앱 설치가 전제.
//         PC 에서는 카카오맵 웹 길찾기로 대체된다.
import { useState } from "react";

// ── 서버 호출 공통 함수 ────────────────────────────────────
//    기존에 쓰시던 authFetch 가 있으면 그걸로 바꾸세요.
//    (예: import { authFetch } from "../api/authFetch")
async function api(path, body) {
  const token = localStorage.getItem("token") || localStorage.getItem("accessToken");
  const res = await fetch(path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  return res.json();
}

// ── 초 → "21분" 같은 사람이 읽는 형태로 ────────────────────
function fmtDur(sec) {
  if (!sec) return "-";
  const m = Math.round(sec / 60);
  if (m < 60) return `${m}분`;
  return `${Math.floor(m / 60)}시간 ${m % 60}분`;
}

// ── 미터 → "4.3km" ─────────────────────────────────────────
function fmtDist(m) {
  if (!m) return "-";
  return m < 1000 ? `${m}m` : `${(m / 1000).toFixed(1)}km`;
}

export default function TourPlanner({ selected = [], onClose }) {
  // 방문 후보 목록. 매물장에서 체크한 것 + 사용자가 주소로 추가한 것
  const [stops, setStops] = useState(() =>
    selected.map((p) => ({
      id: p.estate_no ?? p.id,
      name: p.estate_name || p.shop_name || p.roadAddress || "이름 없음",
      address: p.roadAddress || p.jibunAddress || "",
      lat: p.lat,
      lng: p.lng,
    }))
  );

  const [result, setResult] = useState(null);   // 서버가 돌려준 최적 순서
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [visited, setVisited] = useState([]);   // 방문 완료한 id 목록
  const [skipped, setSkipped] = useState([]);   // 이번엔 건너뛴 id 목록 (복원 가능)

  // 주소 추가용
  const [q, setQ] = useState("");
  const [cands, setCands] = useState([]);       // 주소 검색 후보

  // ── 현재 위치 받아서 최적 순서 계산 ──────────────────────
  //    visited 에 든 곳은 빼고 계산한다 → "다시 계산"이 남은 곳만 재배열
  const optimize = () => {
    setErr("");
    // 완료·건너뜀은 빼고 남은 곳만 다시 배열한다
    const remain = stops.filter(
      (s) => !visited.includes(s.id) && !skipped.includes(s.id)
    );
    if (remain.length === 0) return setErr("방문할 곳이 없습니다");

    if (!navigator.geolocation) return setErr("이 브라우저는 위치를 지원하지 않습니다");

    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await api("/api/tour/optimize", {
            start: { lat: pos.coords.latitude, lng: pos.coords.longitude },
            stops: remain,
            mode: "road",
            priority: "TIME",
            detail: false,    // 지도에 선을 안 그리므로 도로 좌표는 안 받는다
          });
          if (r.ok) setResult(r);
          else setErr(r.message || "계산 실패");
        } catch (e) {
          setErr("서버 연결 실패");
        }
        setBusy(false);
      },
      (e) => {
        // code 1 = 권한 거부, 2 = 위치 확인 불가, 3 = 시간 초과
        setErr(e.code === 1 ? "위치 권한을 허용해 주세요" : "현재 위치를 가져오지 못했습니다");
        setBusy(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  };

  // ── 카카오내비 실행 ──────────────────────────────────────
  //    출발지를 넘기지 않으면 내비가 알아서 "현재 위치"에서 시작한다
  const navigate = (stop) => {
    if (window.Kakao?.Navi) {
      window.Kakao.Navi.start({
        name: stop.name,
        x: stop.lng,       // 카카오는 x = 경도
        y: stop.lat,       //          y = 위도
        coordType: "wgs84",
      });
    } else {
      // PC 이거나 JS SDK 미초기화 → 카카오맵 웹 길찾기로 대체
      const url = `https://map.kakao.com/link/to/${encodeURIComponent(stop.name)},${stop.lat},${stop.lng}`;
      window.open(url, "_blank", "noopener");
    }
  };

  // ── 방문 완료 처리 ───────────────────────────────────────
  //    완료로 표시하면 건너뜀 상태는 자동으로 풀린다(두 상태 동시 불가)
  const markVisited = (id) => {
    setVisited((v) => [...v, id]);
    setSkipped((v) => v.filter((x) => x !== id));
  };

  // ── 건너뛰기 처리 ────────────────────────────────────────
  //    목록에서 지우지 않고 아래로 내린다. 복원 버튼으로 되살릴 수 있다.
  const markSkipped = (id) => {
    setSkipped((v) => [...v, id]);
    setVisited((v) => v.filter((x) => x !== id));
  };

  // ── 완료·건너뜀 해제 (다시 방문 대상으로) ────────────────
  const restore = (id) => {
    setVisited((v) => v.filter((x) => x !== id));
    setSkipped((v) => v.filter((x) => x !== id));
    setResult(null);   // 대상이 늘었으니 순서 재계산 필요
  };

  // ── 목록에서 제거 ────────────────────────────────────────
  const remove = (id) => {
    setStops((s) => s.filter((x) => x.id !== id));
    setVisited((v) => v.filter((x) => x !== id));
    setSkipped((v) => v.filter((x) => x !== id));
    setResult(null);      // 목록이 바뀌면 기존 순서는 무효
  };

  // ── 주소 검색 ────────────────────────────────────────────
  const search = async () => {
    if (!q.trim()) return;
    setErr("");
    const r = await api("/api/tour/geocode", { query: q });
    if (r.ok) setCands(r.list || []);
    else setErr(r.message || "주소 검색 실패");
  };

  // ── 검색 결과를 방문 목록에 추가 ─────────────────────────
  const addStop = (c) => {
    setStops((s) => [...s, { id: `add_${Date.now()}`, ...c }]);
    setCands([]);
    setQ("");
    setResult(null);      // 지점이 늘었으니 순서 재계산 필요
  };

  // 화면에 뿌릴 목록: 계산 전이면 선택 순서, 계산 후면 최적 순서
  const active = stops.filter(
    (s) => !visited.includes(s.id) && !skipped.includes(s.id)
  );
  const list = result ? result.route : active;

  // 아래쪽에 접어둘 항목들 (id 만 있으므로 원본에서 다시 찾는다)
  const parked = stops.filter(
    (s) => visited.includes(s.id) || skipped.includes(s.id)
  );

  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <b style={{ fontSize: 16 }}>
          오늘 동선 ({active.length}곳)
        </b>
        {onClose && <button onClick={onClose} style={S.xBtn}>닫기</button>}
      </div>

      {/* ── 계산 버튼 ── */}
      <button onClick={optimize} disabled={busy} style={S.mainBtn}>
        {busy ? "계산 중..." : result ? "현재 위치에서 다시 계산" : "최적 순서 만들기"}
      </button>

      {err && <div style={S.err}>{err}</div>}

      {/* ── 요약 ── */}
      {result && (
        <div style={S.summary}>
          총 {fmtDist(result.finalDistance)} · 약 {fmtDur(result.finalDuration)}
          {result.apiFailed > 0 && (
            <span style={{ color: "#c0392b" }}> · 일부 구간 추정</span>
          )}
        </div>
      )}

      {/* ── 방문 목록 ── */}
      <ol style={S.list}>
        {list.map((s, i) => (
          <li key={s.id} style={S.item}>
            <div style={S.num}>{result ? s.seq : i + 1}</div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={S.name}>{s.name}</div>
              <div style={S.addr}>{s.address}</div>
              {result && s.legDuration != null && (
                <div style={S.leg}>
                  직전 지점에서 {fmtDist(s.legDistance)} · {fmtDur(s.legDuration)}
                </div>
              )}
            </div>

            <div style={S.btnCol}>
              <button onClick={() => navigate(s)} style={S.naviBtn}>안내</button>
              <div style={S.btnRow}>
                <button onClick={() => markVisited(s.id)} style={S.smBtn}>완료</button>
                <button onClick={() => markSkipped(s.id)} style={S.smBtn}>건너뜀</button>
                <button onClick={() => remove(s.id)} style={S.xSmBtn} title="목록에서 제거">✕</button>
              </div>
            </div>
          </li>
        ))}
      </ol>

      {/* ── 완료 / 건너뜀 한 곳들 (복원 가능) ── */}
      {parked.length > 0 && (
        <div style={S.parkBox}>
          <div style={S.parkHead}>
            완료 {visited.length} · 건너뜀 {skipped.length}
          </div>
          {parked.map((s) => (
            <div key={s.id} style={S.parkItem}>
              <span style={S.badge}>
                {visited.includes(s.id) ? "완료" : "건너뜀"}
              </span>
              <span style={S.parkName}>{s.name}</span>
              <button onClick={() => restore(s.id)} style={S.smBtn}>복원</button>
            </div>
          ))}
        </div>
      )}

      {/* ── 좌표 없어 빠진 매물 ── */}
      {result?.skipped?.length > 0 && (
        <div style={S.err}>
          좌표가 없어 제외됨: {result.skipped.map((s) => s.name).join(", ")}
        </div>
      )}

      {/* ── 주소 직접 추가 ── */}
      <div style={S.addBox}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder="주소나 상호로 지점 추가"
          style={S.input}
        />
        <button onClick={search} style={S.smBtn}>검색</button>
      </div>

      {cands.length > 0 && (
        <ul style={S.cands}>
          {cands.map((c, i) => (
            <li key={i} onClick={() => addStop(c)} style={S.cand}>
              <b>{c.name}</b>
              <span style={S.addr}> {c.address}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── 스타일 (별도 CSS 파일 없이 동작하도록 인라인) ──────────
const S = {
  wrap: { padding: 16, maxWidth: 520, margin: "0 auto", fontSize: 14 },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 },
  mainBtn: { width: "100%", padding: "12px 0", fontSize: 15, fontWeight: 600, border: "1px solid #2a78d6", background: "#2a78d6", color: "#fff", borderRadius: 8, cursor: "pointer" },
  summary: { marginTop: 12, padding: "8px 12px", background: "#f3f4f6", borderRadius: 6, fontSize: 13 },
  err: { marginTop: 10, padding: "8px 12px", background: "#fdecea", color: "#c0392b", borderRadius: 6, fontSize: 13 },
  list: { listStyle: "none", padding: 0, margin: "12px 0 0" },
  item: { display: "flex", gap: 10, alignItems: "flex-start", padding: "12px 0", borderBottom: "1px solid #eee" },
  num: { width: 26, height: 26, flexShrink: 0, borderRadius: "50%", background: "#2a78d6", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600 },
  name: { fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  addr: { fontSize: 12, color: "#777" },
  leg: { fontSize: 12, color: "#2a78d6", marginTop: 2 },
  btnCol: { display: "flex", flexDirection: "column", gap: 5, flexShrink: 0, alignItems: "flex-end" },
  btnRow: { display: "flex", gap: 4 },
  naviBtn: { padding: "6px 14px", fontSize: 13, fontWeight: 600, border: "1px solid #2a78d6", background: "#fff", color: "#2a78d6", borderRadius: 6, cursor: "pointer" },
  smBtn: { padding: "4px 10px", fontSize: 12, border: "1px solid #ccc", background: "#fff", borderRadius: 6, cursor: "pointer" },
  xBtn: { padding: "4px 12px", fontSize: 13, border: "1px solid #ccc", background: "#fff", borderRadius: 6, cursor: "pointer" },
  xSmBtn: { padding: "4px 8px", fontSize: 12, border: "1px solid #ddd", background: "#fff", color: "#999", borderRadius: 6, cursor: "pointer" },
  parkBox: { marginTop: 14, padding: "10px 12px", background: "#f7f7f8", borderRadius: 8 },
  parkHead: { fontSize: 12, color: "#777", marginBottom: 8 },
  parkItem: { display: "flex", alignItems: "center", gap: 8, padding: "5px 0" },
  parkName: { flex: 1, fontSize: 13, color: "#888", textDecoration: "line-through", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  badge: { fontSize: 11, padding: "2px 7px", borderRadius: 10, background: "#e5e7eb", color: "#555", flexShrink: 0 },
  addBox: { display: "flex", gap: 6, marginTop: 16 },
  input: { flex: 1, padding: "8px 10px", border: "1px solid #ccc", borderRadius: 6, fontSize: 14 },
  cands: { listStyle: "none", padding: 0, margin: "8px 0 0", border: "1px solid #eee", borderRadius: 6 },
  cand: { padding: "10px 12px", borderBottom: "1px solid #f3f3f3", cursor: "pointer" },
};
